import { useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Calendar, Tag, Newspaper, FileText, Video, Mic, Loader2 } from 'lucide-react';
import { useInTheNews } from '../components/InTheNews';

// Tag icon mapping
const tagIcons = {
  News: Newspaper,
  Article: FileText,
  Video: Video,
  Interview: Mic,
};

export default function InTheNewsPage() {
  const { newsItems, isLoading } = useInTheNews();
  const [selectedTag, setSelectedTag] = useState('All');

  const allTags = ['All', ...new Set(newsItems.flatMap(item => item.tags || []))];
  
  const filteredNews = selectedTag === 'All' 
    ? newsItems 
    : newsItems.filter(item => item.tags?.includes(selectedTag));

  return (
    <div data-testid="in-the-news-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-brand-accent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-brand-secondary font-semibold tracking-widest uppercase text-sm mb-4">
              Media Coverage
            </p>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-brand-primary mb-6">
              In The News
            </h1>
            <p className="text-slate-600 text-lg">
              Featured articles, interviews, podcasts, and media coverage from various publications.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-brand-secondary" />
            </div>
          ) : newsItems.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 bg-brand-accent rounded-full flex items-center justify-center mx-auto mb-6">
                <Newspaper className="w-10 h-10 text-brand-secondary/40" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-brand-primary mb-4">
                Coming Soon
              </h2>
              <p className="text-slate-600 max-w-md mx-auto">
                Media coverage and news articles will be displayed here. Add content in Contentful to show them.
              </p>
            </div>
          ) : (
            <>
              {/* Tag Filters */}
              {allTags.length > 1 && (
                <div className="flex flex-wrap justify-center gap-2 mb-12">
                  {allTags.map(tag => {
                    const TagIcon = tagIcons[tag] || Tag;
                    return (
                      <button
                        key={tag}
                        onClick={() => setSelectedTag(tag)}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          selectedTag === tag
                            ? 'bg-brand-secondary text-white'
                            : 'bg-brand-accent text-brand-primary hover:bg-brand-secondary/10'
                        }`}
                      >
                        {tag !== 'All' && <TagIcon className="w-4 h-4" />}
                        {tag}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* News Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredNews.map((item, index) => (
                  <motion.a
                    key={item.id}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="group block bg-brand-accent rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                  >
                    {/* Thumbnail */}
                    {item.thumbnail ? (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={item.thumbnail}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    ) : (
                      <div className="aspect-video bg-brand-primary/5 flex items-center justify-center">
                        <Newspaper className="w-12 h-12 text-brand-secondary/30" />
                      </div>
                    )}
                    
                    <div className="p-6">
                      {/* Tags */}
                      {item.tags && item.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {item.tags.map(tag => {
                            const TagIcon = tagIcons[tag] || Tag;
                            return (
                              <span
                                key={tag}
                                className="inline-flex items-center gap-1 text-xs font-medium text-brand-secondary bg-brand-secondary/10 px-2 py-1 rounded-full"
                              >
                                <TagIcon className="w-3 h-3" />
                                {tag}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Title */}
                      <h3 className="font-heading text-xl font-bold text-brand-primary group-hover:text-brand-secondary transition-colors mb-3 line-clamp-2">
                        {item.title}
                      </h3>
                      
                      {/* Description */}
                      {item.description && (
                        <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                          {item.description}
                        </p>
                      )}
                      
                      {/* Date & Link */}
                      <div className="flex items-center justify-between text-sm">
                        {item.date && (
                          <span className="flex items-center gap-1 text-slate-500">
                            <Calendar className="w-4 h-4" />
                            {new Date(item.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-brand-secondary font-medium">
                          Read More <ExternalLink className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </motion.a>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
